import { Archive, Pencil, Play, RotateCcw, Pause } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUpdateLocalCustomerMutation } from "@/api/query-hooks";
import type { CustomerDirectoryItem } from "@/api/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type CustomerStatus = "ACTIVE" | "SUSPENDED" | "ARCHIVED";

export function LocalCustomerActions({ customer }: { customer: CustomerDirectoryItem }) {
  const { t } = useTranslation("licensing");
  const mutation = useUpdateLocalCustomerMutation(customer.id);
  const [editing, setEditing] = useState(false);
  const [number, setNumber] = useState(customer.number);
  const [name, setName] = useState(customer.name);
  const [pendingStatus, setPendingStatus] = useState<CustomerStatus | null>(null);

  const update = (status: CustomerStatus, nextNumber = customer.number, nextName = customer.name) => {
    mutation.mutate({
      clientId: customer.id,
      number: nextNumber,
      name: nextName,
      status,
      expectedRevision: customer.revision,
      requestId: crypto.randomUUID()
    }, {
      onSuccess: () => {
        setEditing(false);
        setPendingStatus(null);
        toast.success(t("customers.updated"));
      },
      onError: (error) => toast.error(error instanceof Error ? error.message : t("customers.updateFailed"))
    });
  };

  return (
    <>
      <div className="flex flex-wrap justify-end gap-2">
        {customer.status !== "ARCHIVED" ? (
          <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={() => {
            setNumber(customer.number);
            setName(customer.name);
            setEditing(true);
          }}><Pencil className="h-3.5 w-3.5" />{t("customers.edit")}</Button>
        ) : null}
        {customer.status === "ACTIVE" ? (
          <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={() => setPendingStatus("SUSPENDED")}><Pause className="h-3.5 w-3.5" />{t("customers.suspend")}</Button>
        ) : null}
        {customer.status === "SUSPENDED" ? (
          <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={() => setPendingStatus("ACTIVE")}><Play className="h-3.5 w-3.5" />{t("customers.activate")}</Button>
        ) : null}
        {customer.status === "ARCHIVED" ? (
          <Button type="button" size="sm" variant="outline" disabled={mutation.isPending} onClick={() => setPendingStatus("SUSPENDED")}><RotateCcw className="h-3.5 w-3.5" />{t("customers.restore")}</Button>
        ) : (
          <Button type="button" size="sm" variant="destructive" disabled={mutation.isPending} onClick={() => setPendingStatus("ARCHIVED")}><Archive className="h-3.5 w-3.5" />{t("customers.archive")}</Button>
        )}
      </div>

      <Dialog open={editing} onOpenChange={(open) => { if (!mutation.isPending) setEditing(open); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("customers.editTitle")}</DialogTitle>
            <DialogDescription>{t("customers.editDescription")}</DialogDescription>
          </DialogHeader>
          <form className="grid gap-4" onSubmit={(event) => {
            event.preventDefault();
            const nextNumber = number.trim();
            const nextName = name.trim();
            if (!nextNumber || !nextName || mutation.isPending) return;
            update(customer.status, nextNumber, nextName);
          }}>
            <div className="grid gap-2"><Label htmlFor="edit-customer-number">{t("customers.number")}</Label><Input id="edit-customer-number" value={number} maxLength={120} onChange={(event) => setNumber(event.target.value)} autoFocus /></div>
            <div className="grid gap-2"><Label htmlFor="edit-customer-name">{t("customers.name")}</Label><Input id="edit-customer-name" value={name} maxLength={200} onChange={(event) => setName(event.target.value)} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="ghost" disabled={mutation.isPending} onClick={() => setEditing(false)}>{t("customers.cancel")}</Button><Button type="submit" disabled={mutation.isPending || !number.trim() || !name.trim()}>{t("customers.save")}</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={pendingStatus !== null} onOpenChange={(open) => { if (!open && !mutation.isPending) setPendingStatus(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pendingStatus === "ARCHIVED" ? t("customers.archiveTitle") : t("customers.statusTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{pendingStatus === "ARCHIVED" ? t("customers.archiveDescription") : t("customers.statusDescription", { status: pendingStatus })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutation.isPending}>{t("customers.cancel")}</AlertDialogCancel>
            <AlertDialogAction disabled={mutation.isPending || pendingStatus === null} onClick={() => { if (pendingStatus) update(pendingStatus); }}>{t("customers.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
